import { Card, ScrollArea } from '@mantine/core';
import { NavigationWrapper } from '../../components/navigation/NavigationWrapper';
import { useApp } from '../../context/AppContext';
import { useViewportSize } from '@mantine/hooks';
import HairCheck from '../../components/video/HairCheck';
import {
  DailyProvider,
  useDailyEvent,
  useParticipantIds,
  DailyAudio,
} from '@daily-co/daily-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { useLocation, useNavigate } from 'react-router-dom';
import { OtherParticipantNotAvailable } from './components/videoCall/ParticipantNotAvailable';
import { VideoCallButtons } from './components/videoCall/VideoCallButtons';
import { RemoteParticipantVideoFeed } from './components/videoCall/RemoteParticipantVideoFeed';
import { LocalParticipantVideoFeed } from './components/videoCall/LocalParticipantVideoFeed';
import { VideoCallError } from './components/videoCall/VideoCallError';
import { VideoFeedType, VideoStatus } from './components/videoCall/types';
import { CallStatusBadge } from './components/videoCall/CallStatusBadge';
import { CallEnded } from './components/videoCall/CallEnded';
import { useMutation } from '@tanstack/react-query';
import { postFeedback } from '../../services/FeedbackService';
import { FeedbackFormSheet } from './components/bookings/FeedbackFormSheet';
import { SuccessModal } from '../../components/modals/SuccessModal';
import { ErrorModal } from './components/error/ErrorModal';

export const DailyVideoCallPage = () => {
  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [videoUsername, setVideoUsername] = useState<string>('');
  const [profilePicture, setProfilePicture] = useState<string>('');
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [url, setUrl] = useState<string>('');
  const [bookingId, setBookingId] = useState<number | null>(null);
  const [professionalId, setProfessionalId] = useState<string>('');
  const [userFeedbackGiven, setUserFeedbackGiven] = useState(false);
  const [professionalFeedbackGiven, setProfessionalFeedbackGiven] =
    useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [rate, setRate] = useState(0);
  const location = useLocation();

  // unmount
  useEffect(() => {
    return () => {
      const callObject = DailyIframe.getCallInstance();
      if (callObject) {
        callObject
          .leave()
          .catch((err) => {
            console.error('error cleaning up callObject', err);
          })
          .finally(() => {
            callObject.destroy();
          });
      }
    };
  }, []);

  useEffect(() => {
    const initializeCall = async () => {
      const state = location.state as any;
      if (state) {
        setVideoUsername(state.username);
        setUrl(state.videoUrl);
        // setStartTime(new Date(state.startTime));
        setEndTime(new Date(state.endTime));
        setProfilePicture(state.profilePicture);
        setBookingId(state.bookingId ?? null);
        setProfessionalId(state.professionalId ?? '');
        setUserFeedbackGiven(Boolean(state.userFeedbackGiven));
        setProfessionalFeedbackGiven(Boolean(state.professionalFeedbackGiven));
        setAccepted(Boolean(state.accepted));
        setIsCancelled(Boolean(state.isCancelled));
        setPaymentStatus(state.paymentStatus ?? '');
        setRate(Number(state.rate ?? 0));
      }
    };
    initializeCall();
  }, [location.state]);

  return (
    <DailyProvider url={url} callObject={callObject}>
      <NavigationWrapper disableScroll hideMobileTaskBar>
        <ActualVideoCall
          callObject={callObject}
          setCallObject={setCallObject}
          url={url}
          videoUsername={videoUsername}
          profilePicture={profilePicture}
          endTime={endTime}
          bookingId={bookingId}
          professionalId={professionalId}
          userFeedbackGiven={userFeedbackGiven}
          professionalFeedbackGiven={professionalFeedbackGiven}
          accepted={accepted}
          isCancelled={isCancelled}
          paymentStatus={paymentStatus}
          rate={rate}
        />
      </NavigationWrapper>
    </DailyProvider>
  );
};

const ActualVideoCall = ({
  callObject,
  setCallObject,
  url,
  videoUsername,
  profilePicture,
  endTime,
  bookingId,
  professionalId,
  userFeedbackGiven,
  professionalFeedbackGiven,
  accepted,
  isCancelled,
  paymentStatus,
  rate,
}: {
  callObject: DailyCall | null;
  setCallObject: (callObject: DailyCall | null) => void;
  url?: string;
  videoUsername: string;
  profilePicture: string;
  endTime: Date | null;
  bookingId: number | null;
  professionalId: string;
  userFeedbackGiven: boolean;
  professionalFeedbackGiven: boolean;
  accepted: boolean;
  isCancelled: boolean;
  paymentStatus: string;
  rate: number;
}) => {
  const [videoFeedType, setVideoFeedType] = useState<VideoFeedType>(
    VideoFeedType.VIDEO
  );
  const [videoStatus, setVideoStatus] = useState<VideoStatus>(
    VideoStatus.HAIRCHECK
  );
  const [videoError, setVideoError] = useState<string | null>(null);
  const [userMediaError, setUserMediaError] = useState(false);
  const [joinedAt, setJoinedAt] = useState<number | null>(null);
  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const closeAndResetFeedbackForm = () => {
    setShowFeedbackSheet(false);
    setRating(0);
    setComment('');
  };
  const navigate = useNavigate();

  const { isTabletAndBelow, profile, token } = useApp();
  const { height: viewportHeight } = useViewportSize();

  const scrollArea = useMemo(() => {
    // DailyVideoCall hides the mobile task bar so only the top bar (50px)
    // contributes to overhead on mobile; desktop header is 80px.
    return isTabletAndBelow ? viewportHeight - 50 : viewportHeight - 110;
  }, [isTabletAndBelow, viewportHeight]);

  useDailyEvent(
    'camera-error',
    useCallback(() => {
      setUserMediaError(true);
    }, [])
  );

  useDailyEvent(
    'joined-meeting',
    useCallback(() => {
      setJoinedAt(Date.now());
      setVideoStatus(VideoStatus.JOINED);
    }, [callObject])
  );

  const hasCurrentUserFeedback = useMemo(() => {
    if (!profile?.id || !professionalId) {
      return false;
    }

    return profile.id === professionalId
      ? professionalFeedbackGiven
      : userFeedbackGiven;
  }, [profile?.id, professionalId, professionalFeedbackGiven, userFeedbackGiven]);

  const shouldShowCallFeedback = useCallback(() => {
    if (!joinedAt || !bookingId || hasCurrentUserFeedback) {
      return false;
    }

    const isPaidOrFree = rate === 0 || paymentStatus === 'succeeded';
    if (!accepted || isCancelled || !isPaidOrFree) {
      return false;
    }

    const callDurationMs = Date.now() - joinedAt;
    return callDurationMs > 10 * 60 * 1000;
  }, [
    accepted,
    bookingId,
    hasCurrentUserFeedback,
    isCancelled,
    joinedAt,
    paymentStatus,
    rate,
  ]);

  useDailyEvent(
    'left-meeting',
    useCallback(() => {
      setShowFeedbackSheet(shouldShowCallFeedback());
      setVideoStatus(VideoStatus.IDLE);
    }, [shouldShowCallFeedback])
  );

  useDailyEvent(
    'error',
    useCallback((event) => {
      console.error('Daily error event:', event);
      setVideoStatus(VideoStatus.ERROR);
      setVideoError(event.errorMsg);
    }, [])
  );

  useEffect(() => {
    const setupCallObject = async () => {
      if (url) {
        const existingCallObject = DailyIframe.getCallInstance();
        if (existingCallObject) {
          console.log('existing call object found, preparing for call...');
          setCallObject(existingCallObject);
          setVideoStatus(VideoStatus.HAIRCHECK);
        } else {
          try {
            const callObj = DailyIframe.createCallObject({
              audioSource: true,
              videoSource: true,
            });
            setCallObject(callObj);
            setVideoStatus(VideoStatus.HAIRCHECK);

            // Set up error listener before attempting to initialize media
            callObj.on('error', (event) => {
              console.error('Daily.co error during setup:', event);
              setVideoStatus(VideoStatus.ERROR);
              setVideoError(event?.errorMsg || 'An error occurred');
            });

            // Request media permissions and prepare for call
            try {
              await callObj.startCamera();
            } catch (error) {
              console.error('Error accessing camera/microphone:', error);
              setVideoStatus(VideoStatus.ERROR);
              setVideoError(
                'Unable to access camera or microphone. Please check permissions.'
              );
            }
          } catch (error) {
            console.error('Error creating call object:', error);
            setVideoStatus(VideoStatus.ERROR);
            setVideoError('Failed to initialize video call');
          }
        }
      }
    };
    setupCallObject();
  }, [url]);

  const joinCall = useCallback(
    (username?: string) => {
      if (callObject) {
        callObject
          .join({
            url,
            userName: username || 'Test wire in later',
            // Force audio and video to be enabled, overriding room defaults
            audioSource: true,
            videoSource: true,
            startAudioOff: false,
            startVideoOff: false,
          })
          .then(() => {
            // Double-check audio is enabled after join
            setTimeout(() => {
              if (!callObject.localAudio()) {
                console.warn('Audio is off, attempting to enable...');
                callObject.setLocalAudio(true);
              }
            }, 500);
            setVideoStatus(VideoStatus.JOINED);
          })
          .catch((err) => {
            console.error('Error joining call:', err);
            setVideoStatus(VideoStatus.ERROR);
            setVideoError(err?.message || 'Failed to join call');
          });
      }
    },
    [callObject, url]
  );

  const isCommentValid = comment.length <= 500;
  const canSubmit = rating >= 1 && rating <= 5 && isCommentValid;

  const {
    mutate: submitFeedback,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      if (!bookingId) {
        throw new Error('Missing bookingId for feedback submission.');
      }

      await postFeedback(
        {
          bookingId,
          rating,
          comment: comment.trim(),
        },
        token || ''
      );
    },
    onSuccess: () => {
      setShowFeedbackSheet(false);
      setShowSuccess(true);
      setRating(0);
      setComment('');
    },
    onError: (error) => {
      setShowFeedbackSheet(false);
      console.error('Error submitting call feedback:', error);
    },
  });

  let content: React.ReactNode;

  if (videoStatus === VideoStatus.ERROR || videoError) {
    content = <VideoCallError message={videoError ?? ''} />;
  } else {
    switch (videoStatus) {
      case VideoStatus.HAIRCHECK:
        content = (
          <VideoCallScaffold height={scrollArea}>
            <HairCheck joinCall={joinCall} cancelCall={() => navigate(-1)} />
          </VideoCallScaffold>
        );
        break;
      case VideoStatus.JOINED:
        content = (
          <VideoCallScaffold height={scrollArea}>
            <JoinedCallWrapper
              height={scrollArea}
              endTime={endTime!}
              participantName={videoUsername}
              participantProfilePicture={profilePicture}
            />
          </VideoCallScaffold>
        );
        break;
      default:
        content = (
          <VideoCallScaffold height={scrollArea}>
            <CallEnded
              participantName={videoUsername}
              participantProfilePicture={profilePicture}
            />
          </VideoCallScaffold>
        );
    }
  }

  return (
    <>
      {content}
      <ErrorModal
        isInError={isError}
        errorMessage="An error has occurred. Please try again."
        dismissAction={() => reset()}
        retryAction={() => submitFeedback()}
        isLoading={isPending}
        testId="call-feedback-error-modal"
      />

      <SuccessModal
        open={showSuccess}
        onClose={() => {
          setShowSuccess(false);
        }}
        title="Thank you"
        message="Thank you for your feedback"
      />

      <FeedbackFormSheet
        opened={showFeedbackSheet}
        onClose={closeAndResetFeedbackForm}
        rating={rating}
        onRatingChange={setRating}
        comment={comment}
        onCommentChange={setComment}
        isSubmitting={isPending}
        canSubmit={canSubmit}
        onSubmit={() => submitFeedback()}
        testIdPrefix="call-feedback"
      />
    </>
  );
};

export const VideoCallScaffold = ({
  children,
  height,
}: {
  children: React.ReactNode;
  height: number;
}) => {
  const { isTabletAndBelow } = useApp();

  return (
    <Card
      w="100%"
      maw={1500}
      h={height}
      shadow="lg"
      padding={0}
      m={isTabletAndBelow ? 0 : 'md'}
      style={{ justifySelf: 'center' }}
    >
      <ScrollArea h={height} w="100%">
        {children}
      </ScrollArea>
    </Card>
  );
};

export const JoinedCallWrapper = ({
  participantName,
  participantProfilePicture,
  endTime,
  height,
}: {
  participantName: string;
  participantProfilePicture: string;
  endTime: Date;
  height: number;
}) => {
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const [isFill, setIsFill] = useState<boolean>(false);

  if (remoteParticipantIds.length > 0) {
    return (
      <div
        style={{
          position: 'relative',
          height: height,
          width: '100%',
        }}
      >
        <DailyAudio />
        <RemoteParticipantVideoFeed
          isFill={isFill}
          endTime={endTime}
          participantSessionId={remoteParticipantIds[0]}
          participantName={participantName}
          participantProfilePicture={participantProfilePicture}
        />
        <LocalParticipantVideoFeed />
        <VideoCallButtons onFitScreen={setIsFill} isFill={isFill} />
      </div>
    );
  }

  if (remoteParticipantIds.length === 0) {
    return (
      <div
        style={{
          position: 'relative',
          height: height - 20,
          width: '100%',
        }}
      >
        <DailyAudio />
        <CallStatusBadge
          endtime={endTime}
          name={participantName}
          sessionId={remoteParticipantIds[0]}
        />
        <LocalParticipantVideoFeed />
        <OtherParticipantNotAvailable
          message="Is not on the call"
          participantName={participantName}
          participantProfilePicture={participantProfilePicture}
        />
        <VideoCallButtons isFill={isFill} onFitScreen={setIsFill} />
      </div>
    );
  }
};

export const CallerNameAndTimeRemaining = () => {};
